import { Directive, effect, inject, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CustomFormGroup, TagFieldValue } from '@eventlyte/forms';
import {
	ApiErrorList,
	ApiRecord,
	REGISTRATION_SERVICE,
	RegistrationPageBase,
	RegistrationStep
} from '@eventlyte/registration';
import { AppRoutePaths } from '../app.routes';

@Directive()
export class LocalRegistrationPageBase extends RegistrationPageBase {
	protected AppRoutePaths = AppRoutePaths;
	protected showServerErrors = signal(false);
	protected serverErrorList = signal<ApiErrorList | null>(null);
	protected saving = signal(false);
	protected registrationService = inject(REGISTRATION_SERVICE);
	protected steps = signal<RegistrationStep[]>([]);
	protected exclusiveTagValueByKey: Readonly<Record<string, string>> = {};
	protected isLoading = signal(false);
	protected activatedRoute = inject(ActivatedRoute);
	protected validationFormGroups: CustomFormGroup[] = [];

	constructor() {
		super();

		effect(() => this.record());
	}

	public override ngOnInit(): void {
		super.ngOnInit();

		this.translocoService.setActiveLang('en');
		this.steps.set(super.getSteps());

		const params = this.activatedRoute.snapshot.queryParamMap;

		if (
			params.get('ref') &&
			params.get('id') &&
			(this.record()['id'] === undefined || this.record()['id'] !== params.get('id'))
		) {
			this.isLoading.set(true);

			this.registrationService.info(params.get('id')!, params.get('ref')!).subscribe({
				next: (registration) => {
					if (registration) {
						registration.record['confirmEmailAddress'] =
							registration.record['emailAddress'];
						this.record.set(registration.record);

						this.recordStateService.saveRecord(
							new ApiRecord({
								record: registration.record,
								extraRecords: registration.extraRecords
							})
						);

						const currentPath = this.activatedRoute.snapshot.routeConfig?.path;

						if (
							currentPath !== AppRoutePaths.RegistrationConfirmation.substring(1) &&
							registration.record['onlineRegistrationCompleted'] === true
						) {
							this.router.navigate([AppRoutePaths.RegistrationAlreadyRegistered]);
						}

						if (
							!registration.record['mobileNumber'] ||
							registration.record['mobileNumber']?.toString().length === 0
						) {
							this.setRecordValue(this.record, 'mobileNumber', '+852');
							this.setRecordValue(this.record, 'phoneOfSecretary', '+852');
						}
					} else {
						this.router.navigate([AppRoutePaths.LandingPage]);
					}
				},
				error: () => {
					this.router.navigate([AppRoutePaths.LandingPage]);
				}
			});
		} else if (params.get('id') === undefined || params.get('ref') === undefined) {
			this.router.navigate([AppRoutePaths.LandingPage]);
		}
	}

	protected override getSteps(): RegistrationStep[] {
		this.record();

		const steps = super.getSteps();

		if (
			this.getRecordValue(this.record, 'bgovBmInterestedInBusinessMatching')?.value ===
			'Interested'
		) {
			return steps;
		}

		return steps.filter((step) => step.route !== AppRoutePaths.RegistrationMatching);
	}

	protected override setRecordValue(
		record: WritableSignal<Record<string, unknown>>,
		key: string,
		value: unknown | undefined
	): void {
		const exclusiveValue = this.exclusiveTagValueByKey[key];

		if (exclusiveValue && this.applyExclusiveTagValueRule(record, key, value, exclusiveValue)) {
			return;
		}

		super.setRecordValue(record, key, value);
	}

	private applyExclusiveTagValueRule(
		record: WritableSignal<Record<string, unknown>>,
		key: string,
		value: unknown | undefined,
		exclusiveValue: string
	): boolean {
		if (!Array.isArray(value)) {
			return false;
		}

		const newValues = value as TagFieldValue<string>[];

		if (newValues.length <= 1) {
			return false;
		}

		const currentValues = this.getRecordValue(record, key) as
			TagFieldValue<string>[] | undefined;
		const newHasExclusiveValue = newValues.some((x) => x.value === exclusiveValue);
		const currentIsOnlyExclusiveValue =
			Array.isArray(currentValues) &&
			currentValues.length === 1 &&
			currentValues[0]?.value === exclusiveValue;

		if (!newHasExclusiveValue) {
			return false;
		}

		if (currentIsOnlyExclusiveValue) {
			// User is selecting another option while the exclusive value is active,
			// so drop the exclusive value and keep the selected options.
			super.setRecordValue(
				record,
				key,
				newValues.filter((x) => x.value !== exclusiveValue)
			);
			return true;
		}

		// Selecting the exclusive value should clear all other values.
		super.setRecordValue(record, key, [new TagFieldValue<string>({ value: exclusiveValue })]);
		return true;
	}
}
